import _ from 'lodash';
import moment from 'moment';
import pMap from 'p-map';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { useCreateEventMaterial } from '../../../../hooks/api/event-material/useCreateEventMaterial';
import { useEventMaterials } from '../../../../hooks/api/event-material/useEventMaterials';
import { useRemoveEventMaterial } from '../../../../hooks/api/event-material/useRemoveEventMaterial';
import { useUpdateEventMaterials } from '../../../../hooks/api/event-material/useUpdateEventMaterials';
import { useMaterial } from '../../../../hooks/api/materials/useMaterial';
import { useMaterials } from '../../../../hooks/api/materials/useMaterials';
import { EventMaterial } from '../../../../hooks/api/utils/types';
import { arrayToMap } from '../../../../libs/arrays';
import { defaultError, defaultSuccess } from '../../../../libs/utils';
import { selectEvent } from '../../../../redux/preferences/selectors';
import { notify } from '../../../../services/bugsnag';
import FullCalendar from '../../../forms/fields/FullCalendar';

export type MaterialsByDayFormProps = {
  materialId: number;
  eventId: number;
  onSubmitSuccess: Function;
};

type EventMaterialItem = {
  amount: number;
  amountRequested?: number;
  day: string;
  originalId?: number;
};

export const MaterialByDayForm = ({ materialId, eventId, onSubmitSuccess }: MaterialsByDayFormProps) => {
  const { data: m, isLoading: mLoading } = useMaterial(materialId);
  const { data: em, isLoading: emLoading } = useEventMaterials(eventId, materialId);

  const event = useSelector(selectEvent);
  const calendarRef = useRef<any>();

  const { validRange } = useMemo(() => {
    let validRange = undefined;
    if (event.startsAt) {
      validRange = {
        start: event.startsAt.split(' ')[0],
        end: event.endsAt.split(' ')[0],
      };
    }
    return {
      validRange,
    };
  }, [event]);

  const { mutateAsync: createEm } = useCreateEventMaterial(eventId, materialId);
  const { mutateAsync: updateEm } = useUpdateEventMaterials(eventId);
  const { mutateAsync: removeEm } = useRemoveEventMaterial(eventId);

  const [values, setValues] = useState<Map<string, EventMaterialItem>>(new Map());
  // const currentEventId = useRef<number>()
  // const currentMaterialId = useRef<number>()
  // console.log('e', { currentEventId: currentEventId.current, eventId, currentMaterialId: currentMaterialId.current, materialId })

  const onSubmit = async (e: any) => {
    // here we should diff the original eventmaterials data with the updated data. not easy but doable.
    // Then we throw a bunch of mutations over to the API, preferably with a promise-map thingy.
    // on error, we reload the data and rerender to show what WAS succesful.
    e.preventDefault();
    if (!em) return;
    const { created, updated, removed } = diffChanges(em.data, Array.from(values.values()));
    try {
      await pMap(removed, (item) => removeEm({ eventMaterialId: item.id }));
      await pMap(updated, (item) =>
        updateEm({
          eventMaterialId: item.id,
          data: { amount: item.amount, day: item.day, materialId },
        })
      );
      await pMap(created, (item) => createEm({ data: { day: item.day, amount: item.amount, materialId } }));
    } catch (e) {
      // @ts-ignore
      notify(e);
      // @ts-ignore
      defaultError(`Failed to update event materials: ${e.message}`);
      return;
    }

    defaultSuccess('Update successful');
    onSubmitSuccess();
    return false;
  };

  const remove = (day: string) => {
    values.delete(day);
    setValues(new Map(values));
  };

  const change = (source: any, day: string, amount: number) => {
    const v = values.get(day);
    if (v) v.amount = amount;
    setValues(new Map(values));
  };

  const handleDateSelection = (startObj: any, endObj: any) => {
    const startDay = moment(startObj);
    const length = Math.abs(startDay.diff(moment(endObj), 'days'));
    Array.from({ length }).forEach((_, index) => {
      const day = startDay.clone().add(index, 'days').format('YYYY-MM-DD');

      if (!values.get(day)) {
        values.set(day, { day, amount: 1 });
      }

      setValues(new Map(values));
    });
  };

  // const handleDateEdit = (event: any) => {
  //
  //   const targetDate = event.start.format('YYYY-MM-DD')
  //   const value = values.get(event.day)
  //   if (value && !values.get(targetDate)) {
  //     value.day = targetDate
  //     values.delete(event.day)
  //     values.set(value.day, value)
  //     setValues(new Map(values))
  //   } else {
  //       // rollback the last change. This is hacky as f*ck, but we're very limited by this weird calendar implementation.
  //       calendarRef.current._forceRerender()
  //   }
  // }

  const array = Array.from(values.values());

  const calEvents = array.map((f, index) => ({
    start: f.day,
    day: f.day,
    allDay: true,
    title: f.amount + '',
  }));

  // set or reset the values as things change
  useEffect(() => {
    if (!values.size) {
      // only set if there is no data yet. this is a limitation to make flow easier
      // if (!values.size || currentEventId.current !== eventId || currentMaterialId.current !== materialId) { // only set if there is no data yet. this is a limitation to make flow easier
      // currentEventId.current = eventId
      // currentMaterialId.current = materialId
      setValues(
        em?.data.reduce((acc, em) => {
          acc.set(em.day, {
            amount: em.amount,
            amountRequested: em.amountRequested,
            day: em.day,
            originalId: em.id,
          });
          return acc;
        }, new Map<string, EventMaterialItem>()) ?? new Map()
      );
      // setValues(em?.data.map(em => ({ amount: em.amount, day: em.day, originalId: em.id, id: em.id })) ?? [])
    }
  }, [em?.data]);

  if (mLoading || emLoading) {
    return <div>Loading.</div>;
  }

  return (
    <form className="m-login__form m-form" onSubmit={onSubmit}>
      <div className="modal-body">
        <div className="row">
          <div className="col-xl-6">
            <div className="form-group m-form__group">
              <input type="hidden" name="eventId" value={eventId} />
              <input type="hidden" name="materialId" value={materialId} />
              <label>Material Name</label>
              <input type="text" disabled value={m?.name ?? ''} style={{ marginLeft: '2em' }} />
              <table className="table m-table m-table--head-separator-primary">
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Capacity</th>
                    <th>Remaining</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {/* .sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime()) */}
                  {array
                    .sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime())
                    .map((eventMaterial, index) => {
                      const remainingCapacity = eventMaterial.amount - (eventMaterial.amountRequested ?? 0);

                      return (
                        <tr key={eventMaterial.day}>
                          <td>{eventMaterial.day}</td>
                          <td>
                            <input
                              type="number"
                              className="form-control m-input"
                              min="1"
                              tabIndex={index}
                              value={eventMaterial.amount}
                              onChange={(e) => {
                                change(e.currentTarget, eventMaterial.day, parseInt(e.target.value, 10));
                              }}
                            />
                          </td>
                          <td style={remainingCapacity < 0 ? { color: 'red' } : {}}>{remainingCapacity}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-outline-danger m-btn m-btn--icon m-btn--icon-only"
                              onClick={() => remove(eventMaterial.day)}
                            >
                              <i className="la la-trash" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="col-xl-6">
            <div className="form-group m-form__group">
              <label>Availability</label>
              <FullCalendar
                validRange={validRange}
                selectable
                editable={false}
                handleSelect={handleDateSelection}
                events={calEvents}
                ref={calendarRef}
              />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <div className="row align-items-center">
            <div className="col-lg-12 m--align-right">
              <button
                className={
                  'btn m-btn btn-primary' + (false ? ' m-btn--custom m-loader m-loader--light m-loader--left' : '')
                }
                type="submit"
                disabled={false}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

function diffChanges(
  em: Array<EventMaterial>,
  fields: Array<EventMaterialItem>
): {
  created: Array<EventMaterialItem>;
  updated: Array<EventMaterial>;
  removed: Array<EventMaterial>;
} {
  const existingByDay = arrayToMap(em, 'day');
  const fieldsByDay = arrayToMap(fields, 'day');
  // does not support day changes in existing events. Delete and recreate as needed.
  const created = fields.filter((f) => !existingByDay.get(f.day));
  const updated = em.filter((f) => {
    const field = fieldsByDay.get(f.day);
    if (field && f.amount !== field.amount) {
      f.amount = field.amount;
      return true;
    }
    return false;
  });
  const removed = em.filter((f) => !fieldsByDay.get(f.day));

  return {
    created,
    updated,
    removed,
  };
}
